import React, { type CSSProperties } from 'react';
import { AlarmClockOutline, DateDefault, LabelOutline, Projects, Repeat, Subtask } from '@doist/icons/product-ui';
import { Text } from '@doist/marketist';
import cn from 'clsx';
import { type TFunction, useTranslation } from 'next-i18next';
import { type ProjectColors } from '../product-ui-list-item/types';
import { ProductUISkeletonText } from '../product-ui-skeleton-text/product-ui-skeleton-text';
import { ProductUITaskCircle } from '../product-ui-task-circle/product-ui-task-circle';
import { type ProductUISkeletonWidth } from '../product-ui-types';
import { mapColorNameToCustomProp } from '../utils';
import css from './product-ui-task.module.css';
type ProductUITaskProps = {
  id?: string;
  className?: string;
  children: React.ReactNode;
  attributeSection?: React.ReactNode;
  contextMenu?: React.ReactNode;
  taskCircleSection?: React.ReactNode;
  projectSection?: React.ReactNode;
  hoveredSection?: React.ReactNode;
  variant?: 'list' | 'board';
  description?: React.ReactNode;
  hovered?: boolean;
  subtask?: boolean;
};
export function ProductUITask({
  id,
  className,
  children,
  taskCircleSection,
  attributeSection,
  contextMenu,
  projectSection,
  hoveredSection,
  variant = 'list',
  description,
  hovered,
  subtask,
  ...rest
}: ProductUITaskProps) {
  return <div className={css.taskContainer} data-sentry-component="ProductUITask" data-sentry-source-file="product-ui-task.tsx">
            <div id={id} className={cn(className, css.task, css[variant], {
      [css.subtask]: subtask
    }, {
      [css.hovered]: hovered
    })} {...rest}>
                <div className={css.taskOverflowContainer}>
                    <div className={css.taskSection}>
                        <div className={css.taskInfo}>
                            <div className={css.taskCircle}>
                                {taskCircleSection ? taskCircleSection : <ProductUITaskCircle />}
                            </div>
                            <div className={css.taskContent}>{children}</div>
                        </div>
                        {projectSection && !hovered ? projectSection : null}
                    </div>
                    {hovered ? hoveredSection : null}
                </div>
                {description ? description : null}
                <div>{attributeSection}</div>
            </div>
            {contextMenu}
        </div>;
}
type ProductUITaskProjectProps = {
  id?: string;
  width?: ProductUISkeletonWidth;
  iconColor?: ProjectColors;
};
interface TaskCustomProperties extends CSSProperties {
  '--basis': string;
}
ProductUITask.Project = function ProductUITaskProject({
  id,
  width = '20',
  iconColor = 'red'
}: ProductUITaskProjectProps) {
  const customProps: TaskCustomProperties = {
    '--basis': `${width}%`
  };
  return <div id={id} className={css.projectSection} style={customProps}>
            <ProductUISkeletonText width="100" />
            <Projects width={18} height={18} className={css.projectIcon} style={{
      color: mapColorNameToCustomProp(iconColor)
    }} />
        </div>;
};
type ProductUITextProps = {
  children: React.ReactNode;
  checked?: boolean;
};
ProductUITask.Text = function ProductUIText({
  children,
  checked
}: ProductUITextProps) {
  return <Text tag="span" size="body-sm" className={cn(css.taskText, {
    [css.checked]: checked
  })}>
            {children}
        </Text>;
};
type ProductUIDescriptionProps = {
  checked?: boolean;
  children: React.ReactNode;
};
ProductUITask.Description = function ProductUIDescription({
  children,
  checked = false,
  ...rest
}: ProductUIDescriptionProps) {
  return <Text tag="div" size="body-2xs" className={cn({
    [css.checked]: checked
  }, css.taskDescription)} {...rest}>
            {children}
        </Text>;
};
ProductUITask.AttributeButtonContainer = function ProductUITaskAttributeButtonContainer({
  children,
  ...rest
}: {
  children: React.ReactNode;
}) {
  return <div className={css.attributeButtonContainer} {...rest}>
            {children}
        </div>;
};
type ProductUITaskAttributeSubTask = {
  variant: 'subtask';
  count: number;
  completedCount: number;
};
type ProductUITaskAttributeDueDate = {
  variant: 'dueDateTomorrow';
  children?: React.ReactNode;
};
type ProductUITaskAttributeDueDateToday = {
  variant: 'dueDateToday';
  children?: React.ReactNode;
};
type ProductUITaskAttributeRepeat = {
  variant: 'repeat';
};
type ProductUITaskAttributeLabel = {
  variant: 'label';
  children: React.ReactNode;
};
type ProductUITaskAttributeReminder = {
  variant: 'reminder';
};
type ProductUITaskAttributeProps = ProductUITaskAttributeSubTask | ProductUITaskAttributeDueDate | ProductUITaskAttributeRepeat | ProductUITaskAttributeLabel | ProductUITaskAttributeReminder | ProductUITaskAttributeDueDateToday;
function getVariantIcon(variant: ProductUITaskAttributeProps['variant']) {
  switch (variant) {
    case 'subtask':
      return Subtask;
    case 'dueDateTomorrow':
      return DateDefault;
    case 'dueDateToday':
      return DateDefault;
    case 'repeat':
      return Repeat;
    case 'label':
      return LabelOutline;
    case 'reminder':
      return AlarmClockOutline;
  }
}
function getVariantChildren(props: ProductUITaskAttributeProps, t: TFunction) {
  switch (props.variant) {
    case 'subtask':
      return t('taskAttributePill.subtasks', {
        count: props.completedCount,
        total: props.count
      });
    case 'dueDateTomorrow':
      return t('taskAttributePill.tomorrowTime', {
        time: props.children
      });
    case 'dueDateToday':
      return props.children ? props.children : t('taskAttributePill.today');
    case 'label':
      return props.children;
    default:
      return null;
  }
}
ProductUITask.AttributeRow = function ProductUiTaskAttributeRow({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className={css.taskAttributeRow}>{children}</div>;
};
ProductUITask.Attribute = function ProductUITaskAttribute(props: ProductUITaskAttributeProps) {
  const {
    t
  } = useTranslation('productui');
  const Icon = getVariantIcon(props.variant);
  const child = getVariantChildren(props, t);
  return <div className={cn(css.taskAttribute, css[props.variant])}>
            <Icon className={css.taskAttributeIcon} width={18} height={18} />
            <Text tag="span" size="body-2xs">
                {child}
            </Text>
        </div>;
};
ProductUITask.AttributeContainer = function ProductUITaskAttributeContainer({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className={css.taskAttributeContainer}>{children}</div>;
};